import { useMemo, useSyncExternalStore } from "react";
import { downloadMediaSelection } from "./downloadMediaToFolder";

const IDLE = {
  status: "idle",
  scopeId: null,
  kind: "media",
  itemIds: [],
  currentId: null,
  currentProgress: 0,
  completedIds: [],
  completedCount: 0,
  totalCount: 0,
  percent: 0,
  completedAt: null,
  saved: 0,
  mode: null,
};

let snapshot = IDLE;
const listeners = new Set();

function emit(next) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function computePercent(completedCount, totalCount, currentProgress) {
  if (!totalCount) return 0;
  return Math.min(
    100,
    Math.round(((completedCount * 100 + (currentProgress || 0)) / (totalCount * 100)) * 100)
  );
}

export function subscribeMediaDownload(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMediaDownloadState() {
  return snapshot;
}

export function dismissMediaDownload(scopeId) {
  if (snapshot.status !== "completed") return;
  if (scopeId && snapshot.scopeId !== scopeId) return;
  emit(IDLE);
}

export function applyDownloadJobToSections(sections, job) {
  if (!job || job.status !== "running") return sections;
  const selected = new Set(job.itemIds);
  const done = new Set(job.completedIds);
  return (sections || []).map((section) => ({
    ...section,
    items: (section.items || []).map((row) => {
      if (!selected.has(row.id)) {
        return {
          ...row,
          isSelect: false,
          isDownloading: false,
          downloadDone: false,
          progress: 0,
        };
      }
      const isCurrent = row.id === job.currentId;
      return {
        ...row,
        isSelect: true,
        isDownloading: isCurrent,
        downloadDone: done.has(row.id) && !isCurrent,
        progress: isCurrent ? job.currentProgress : 0,
      };
    }),
  }));
}

export function startMediaDownload({ scopeId, items, dirHandle, kind = "media" }) {
  if (!scopeId || snapshot.status === "running") {
    return { started: false, busy: snapshot.status === "running" };
  }
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return { started: false, busy: false };

  const promise = runDownloadJob({ scopeId, items: list, dirHandle, kind });
  return { started: true, busy: false, promise };
}

async function runDownloadJob({ scopeId, items, dirHandle, kind }) {
  const itemIds = items.map((item) => item.id);
  emit({
    ...IDLE,
    status: "running",
    scopeId,
    kind,
    itemIds,
    currentId: items[0]?.id || null,
    currentProgress: 0,
    completedIds: [],
    completedCount: 0,
    totalCount: items.length,
    percent: 0,
  });

  const completedIds = [];
  try {
    const result = await downloadMediaSelection({
      items,
      dirHandle,
      onItemProgress: (item, percent, done) => {
        const alreadyDone = completedIds.includes(item.id);
        if (done && !alreadyDone) completedIds.push(item.id);
        const completedCount = completedIds.length;
        emit({
          ...snapshot,
          status: "running",
          currentId: done ? snapshot.currentId : item.id,
          currentProgress: done ? 0 : percent,
          completedIds: [...completedIds],
          completedCount,
          percent: computePercent(completedCount, items.length, done ? 0 : percent),
        });
      },
    });

    if (result?.cancelled) {
      emit(IDLE);
      return result;
    }

    emit({
      ...snapshot,
      status: "completed",
      currentId: null,
      currentProgress: 0,
      completedCount: result?.saved ?? completedIds.length,
      percent: 100,
      completedAt: Date.now(),
      saved: result?.saved ?? completedIds.length,
      mode: result?.mode || null,
    });
    return result;
  } catch (err) {
    console.error("Background media download failed", err);
    emit({
      ...snapshot,
      status: "completed",
      currentId: null,
      currentProgress: 0,
      completedAt: Date.now(),
      percent: computePercent(completedIds.length, items.length, 0),
      saved: completedIds.length,
    });
    return { cancelled: false, saved: completedIds.length };
  }
}

export function useMediaDownload(scopeId) {
  const state = useSyncExternalStore(subscribeMediaDownload, getMediaDownloadState);
  return useMemo(() => {
    const matches = !!scopeId && state.scopeId === scopeId;
    if (!matches) {
      return {
        ...IDLE,
        globalBusy: state.status === "running",
        matches: false,
      };
    }
    return {
      ...state,
      globalBusy: state.status === "running",
      matches: true,
    };
  }, [state, scopeId]);
}
